// eyeq — shipment canon v2 (ADR-0051). Pure, side-effect-free helpers shared by
// the worker's shipment builder and the internal cabinet's xlsx writer, so the
// two can never disagree about a name, an id or a taxonomy spelling.
//
// Three dialects of the same taxonomy coexist and each one is load-bearing:
//   · the JSON-schema dialect ("Fine Art and Museum") — per-file delivery JSON,
//     pinned by MeqAssetMetaSchema;
//   · the underscore dialect ("Fine_Art_Museum") — the xlsx template's own
//     validation lists (its INDIRECT formulas resolve defined names spelled
//     this way), so the table MUST use it;
//   · our internal cat_id slug ("fine_art_and_museum__paintings") — D1 only.
// eyeqUnderscoreName converts dialect 1 → dialect 2 and nothing else.
import { z } from 'zod';

/** Name → the template's underscore spelling. Rules derived from the template's
 *  own defined names: split on whitespace, comma, hyphen and slash; drop the
 *  connector word "and"; join with '_'. Apostrophes stay ("Children's_Toys").
 *  Verified against all 33 categories and 182 sub-categories. */
export function eyeqUnderscoreName(name: string): string {
  return name
    .split(/[\s,\-/]+/)
    .filter((t) => t.length > 0 && t.toLowerCase() !== 'and')
    .join('_');
}

// Client shipment table header row — VERBATIM from the current template's
// ListObject columns. The two "Lightining" typos of the 2026-07 template are
// FIXED in this one; reproduce whatever the template says, never a guess.
export const EYEQ_SHIPMENT_HEADERS_V2 = [
  'Asset_ID', 'subject_name', 'disambiguation_details', 'category',
  'sub_category', 'indoor_outdoor', 'Indoor Lighting', 'Outdoor Lighting',
  'place_details', 'url', 'has_text', 'Language_Code', 'city', 'country',
  'camera_make', 'camera_model', 'filename', 'Participant ID', 'UUID',
] as const;

/** Delivery file name. Numbering restarts at 1 in EVERY shipment and the order
 *  is (Participant ID, UUID) — the client reads the folder as a sequence, so a
 *  gap or a global counter both read as a packing defect. */
export function eyeqShipmentFileName(seq: number, ext: string): string {
  return `IMG_${String(seq).padStart(4, '0')}.${ext || 'bin'}`;
}

/** Asset_ID = <Participant ID>_<file stem>_<UUID>. Composed, never stored as a
 *  free field: every part must stay derivable from the row itself. */
export function eyeqDeliveryAssetId(pid: string, fileName: string, uuidSeq: number): string {
  const stem = fileName.replace(/\.[^.]*$/, '');
  return `${pid}_${stem}_${uuidSeq}`;
}

/** Participant ID out of a locale pool: 4-letter prefix + zero-padded index. */
export function eyeqPid(prefix: string, index: number, width = 5): string {
  return prefix + String(index).padStart(width, '0');
}

/** Pool prefix for a locale — the underscore form uppercased ('pl_PL' → 'PLPL'). */
export function eyeqPidPrefix(locale: string): string {
  return locale.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/** Index back out of a Participant ID (''/malformed → null). */
export function eyeqPidIndex(pid: string): number | null {
  const m = /^([A-Za-z]+)(\d+)$/.exec(pid.trim());
  if (!m) return null;
  return Number(m[2]);
}

// disambiguation_details is a description of the OBJECT. Three QA rounds kept
// finding annotator voice in it instead — "not the target", "the target object
// is …", "this image shows …". Those phrases are meta-commentary about the
// task, they ship straight into the client's table, and they are detectable
// deterministically, so they are a preflight issue rather than a QA opinion.
const DETAILS_META_PHRASES = [
  'not the target',
  'the target',
  'target object',
  'this image',
  'this photo',
  'the photo shows',
  'the image shows',
];

/** null | 'details_style'. */
export function eyeqDetailsStyleNote(details: string): string | null {
  const s = details.toLowerCase();
  return DETAILS_META_PHRASES.some((p) => s.includes(p)) ? 'details_style' : null;
}

/** Google Maps search link for a Business and Storefronts subject — the URL
 *  form the Capture Protocol itself sanctions for that category. Built from
 *  the NAME + city + country, never from coordinates: the index deliberately
 *  keeps location at city/country granularity (ADR-0039 §2), and a link that
 *  carried lat/lon would put precise geolocation back into D1. */
export function eyeqMapsSearchUrl(subject: string, city: string, country: string): string {
  const q = [subject, city, country].map((s) => (s || '').trim()).filter(Boolean).join(' ');
  return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(q);
}

// One shipment row as the ship endpoints emit it (v2 fields carry the canon:
// pool id, per-participant sequence number, delivery file name and Asset_ID).
export const EyeqShipRowSchema = z.object({
  asset_id: z.string(),          // our catalog id ea_<32hex>
  uuid: z.string(),              // RFC-4122 v4 minted at submit (audit trail)
  r2_key: z.string(),
  shipped_file: z.string(),      // IMG_0001.heic
  shipped_json: z.string(),      // IMG_0001.json
  original_filename: z.string(),
  participant_public_id: z.string(), // ep_<12hex> — our opaque participant key
  pid: z.string(),               // Participant ID in the client's pool
  uuid_seq: z.number().int(),    // per-participant running number
  delivery_asset_id: z.string(), // <pid>_IMG_0001_<uuid_seq>
  json: z.record(z.string(), z.unknown()),
});
export type EyeqShipRow = z.infer<typeof EyeqShipRowSchema>;
